import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiGrid, FiUsers, FiDollarSign, FiActivity, FiBell, FiMessageSquare } from 'react-icons/fi';
import api from '../../api';

export default function SuperDashboard() {
  const [stats, setStats] = useState(null);
  const [recentTenants, setRecentTenants] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data } = await api.get('/api/super/stats');
      setStats(data.stats);
      setRecentTenants(data.recentTenants || []);
      setNotifications(data.monthlyNotifications || []);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num || 0);
  };

  if (loading) {
    return <div className="loading-page">로딩 중...</div>;
  }

  const pushCount = notifications.find(n => n._id === 'push')?.count || 0;
  const alimtalkCount = notifications.find(n => n._id === 'alimtalk')?.count || 0;

  return (
    <div className="super-dashboard">
      <div className="page-header">
        <h2>Super Admin 대시보드</h2>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid super-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#DBEAFE' }}>
            <FiGrid style={{ color: '#2563EB' }} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats?.tenants?.total || 0}</div>
            <div className="stat-label">전체 테넌트</div>
            <div className="stat-sub">{stats?.tenants?.active || 0}개 활성</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#D1FAE5' }}>
            <FiUsers style={{ color: '#059669' }} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{formatNumber(stats?.users?.total)}</div>
            <div className="stat-label">전체 사용자</div>
            <div className="stat-sub">{formatNumber(stats?.users?.students)}명 학생</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#FEF3C7' }}>
            <FiDollarSign style={{ color: '#D97706' }} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{formatNumber(stats?.points?.totalBalance)}</div>
            <div className="stat-label">총 포인트 잔액</div>
            <div className="stat-sub">{stats?.pendingCharges || 0}건 충전대기</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#FCE7F3' }}>
            <FiActivity style={{ color: '#DB2777' }} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{formatNumber(pushCount + alimtalkCount)}</div>
            <div className="stat-label">이번달 발송</div>
            <div className="stat-sub">푸시 {pushCount} / 알림톡 {alimtalkCount}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {stats?.pendingCharges > 0 && (
        <div className="alert-banner">
          <FiDollarSign />
          <span>처리 대기 중인 충전 요청이 {stats.pendingCharges}건 있습니다.</span>
          <Link to="/super/charges" className="btn btn-sm btn-primary">확인하기</Link>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Recent Tenants */}
        <div className="section">
          <div className="section-header">
            <h3>최근 가입 테넌트</h3>
            <Link to="/super/tenants" className="btn btn-sm btn-outline">전체보기</Link>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>학원명</th>
                  <th>상태</th>
                  <th>가입일</th>
                </tr>
              </thead>
              <tbody>
                {recentTenants.length === 0 ? (
                  <tr><td colSpan="4" className="empty">가입된 테넌트가 없습니다</td></tr>
                ) : (
                  recentTenants.map((tenant) => (
                    <tr key={tenant._id}>
                      <td><code>{tenant.tenantId}</code></td>
                      <td>{tenant.name}</td>
                      <td>
                        <span className={`status ${tenant.isActive ? 'active' : 'inactive'}`}>
                          {tenant.isActive ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td>{new Date(tenant.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Monthly Stats */}
        <div className="section">
          <h3>이번달 알림 발송 현황</h3>
          <div className="notification-stats">
            <div className="noti-stat-item">
              <FiBell className="noti-icon push" />
              <div className="noti-info">
                <div className="noti-value">{formatNumber(pushCount)}</div>
                <div className="noti-label">푸시 알림</div>
                <div className="noti-cost">{formatNumber(pushCount * 7)}원</div>
              </div>
            </div>
            <div className="noti-stat-item">
              <FiMessageSquare className="noti-icon alimtalk" />
              <div className="noti-info">
                <div className="noti-value">{formatNumber(alimtalkCount)}</div>
                <div className="noti-label">알림톡</div>
                <div className="noti-cost">{formatNumber(alimtalkCount * 40)}원</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Region & Operation Stats */}
      <div className="dashboard-grid" style={{ marginTop: '20px' }}>
        {/* Region Stats */}
        <div className="section">
          <h3>지역별 분포</h3>
          <div className="stats-list">
            {stats?.regionStats && stats.regionStats.length > 0 ? (
              stats.regionStats.map((region, index) => (
                <div key={index} className="stats-list-item">
                  <div className="stats-list-label">{region._id}</div>
                  <div className="stats-list-bar">
                    <div
                      className="stats-list-bar-fill"
                      style={{
                        width: `${(region.count / stats.regionStats[0].count) * 100}%`,
                        background: '#3B82F6'
                      }}
                    ></div>
                  </div>
                  <div className="stats-list-value">{region.count}개</div>
                </div>
              ))
            ) : (
              <div className="empty">지역 정보가 없습니다</div>
            )}
          </div>
        </div>

        {/* Operation Type Stats */}
        <div className="section">
          <h3>운영 체제별 분포</h3>
          <div className="operation-stats">
            {stats?.operationTypeStats && stats.operationTypeStats.length > 0 ? (
              stats.operationTypeStats.map((opType, index) => (
                <div key={index} className="operation-stat-item">
                  <div className="operation-icon">
                    {opType._id === 'solo' ? <FiUsers size={32} /> : <FiUsers size={32} />}
                  </div>
                  <div className="operation-info">
                    <div className="operation-label">
                      {opType._id === 'solo' ? '1인 체제' : '다인 체제'}
                    </div>
                    <div className="operation-value">{opType.count}개</div>
                    <div className="operation-percentage">
                      {stats.tenants.total > 0
                        ? `${Math.round((opType.count / stats.tenants.total) * 100)}%`
                        : '0%'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">운영 체제 정보가 없습니다</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
